import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { notificationApi } from "../api/notificationApi";
import { io } from "socket.io-client";
import { useAuth } from "./AuthContext";

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within NotificationProvider",
    );
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const { user } = useAuth();

  const refreshNotifications = useCallback(async () => {
    if (!user?.UserID || user.MustChangePassword) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    try {
      const response = await notificationApi.getNotifications();
      const items = response.data || [];
      setNotifications(items);
      setUnreadCount(
        response.unreadCount ??
          items.filter((notification) => !notification.IsRead).length,
      );
    } catch (error) {
      console.error("Failed to refresh notifications:", error);
    }
  }, [user?.MustChangePassword, user?.UserID]);

  useEffect(() => {
    if (!user?.UserID || user.MustChangePassword) {
      setNotifications([]);
      setUnreadCount(0);
      setSocket(null);
      setIsConnected(false);
      return undefined;
    }
    refreshNotifications();
    const token = localStorage.getItem("token");
    if (!token) return undefined;

    const connection = io({
      auth: { token },
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
    });
    setSocket(connection);
    connection.on("connect", () => {
      setIsConnected(true);
      refreshNotifications();
    });
    connection.on("connect_error", (error) => {
      console.error("Live updates connection failed:", error.message);
    });
    connection.on("disconnect", () => setIsConnected(false));
    connection.on("notification:new", (notification) => {
      if (notification?.NotificationID) {
        setNotifications((current) => {
          const alreadyPresent = current.some(
            (item) => item.NotificationID === notification.NotificationID,
          );
          if (!alreadyPresent && !notification.IsRead) {
            setUnreadCount((count) => count + 1);
          }
          return [
            notification,
            ...current.filter(
              (item) => item.NotificationID !== notification.NotificationID,
            ),
          ].slice(0, 100);
        });
      } else {
        refreshNotifications();
      }
    });
    connection.on("notification:read", ({ notificationId }) => {
      setNotifications((current) => {
        const unreadCountToRemove = current.filter(
          (item) =>
            (!notificationId || item.NotificationID === notificationId) &&
            !Boolean(item.IsRead),
        ).length;
        if (unreadCountToRemove) {
          setUnreadCount((count) => Math.max(0, count - unreadCountToRemove));
        }
        return current.map((item) =>
          !notificationId || item.NotificationID === notificationId
            ? { ...item, IsRead: true }
            : item,
        );
      });
    });
    return () => {
      connection.disconnect();
      setSocket(null);
      setIsConnected(false);
    };
  }, [refreshNotifications, user?.MustChangePassword, user?.UserID]);

  const markAsRead = async (notificationId) => {
    const wasUnread = notifications.some(
      (item) =>
        item.NotificationID === notificationId && !Boolean(item.IsRead),
    );
    await notificationApi.markRead(notificationId);
    setNotifications((current) =>
      current.map((item) =>
        item.NotificationID === notificationId
          ? { ...item, IsRead: true }
          : item,
      ),
    );
    if (wasUnread && !isConnected) {
      setUnreadCount((current) => Math.max(0, current - 1));
    }
  };

  const markAllAsRead = async () => {
    await notificationApi.markAllRead();
    setNotifications((current) =>
      current.map((item) => ({ ...item, IsRead: true })),
    );
    if (!isConnected) setUnreadCount(0);
  };

  const clearNotifications = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  const value = {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearNotifications,
    refreshNotifications,
    socket,
    isConnected,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
